import { invoiceableLines } from './invoiceableLines';
import type { InvoicingView } from './invoicingView';
import type { SoldLines } from '../types/agreements.types';

export interface RaisePanelState {
    isLoading: boolean;
    isError: boolean;
    lines: SoldLines | undefined;
    selectionCount: number;
}

export interface PreviouslyInvoicedPanelState {
    isLoading: boolean;
    isError: boolean;
    invoiceCount: number;
    canAddInvoice: boolean;
    soldLines: Pick<RaisePanelState, 'isLoading' | 'isError' | 'lines'>;
}

function stillToInvoiceHasFocusable({ isLoading, isError }: PreviouslyInvoicedPanelState['soldLines']): boolean {
    return !isLoading && isError;
}

/** APG: the tabpanel is only a tab stop while its body holds nothing focusable.
 *  Any retry, expand or clear button, or enabled checkbox removes it; Details needs a selection. */
export function isRaisePanelFocusable({
    isLoading,
    isError,
    lines,
    selectionCount,
}: RaisePanelState): boolean {
    if (isLoading) return true;
    if (isError || !lines) return false;
    const shown = invoiceableLines(lines);
    if (shown.services.length > 0 || shown.addOns.length > 0) return false;
    return selectionCount === 0;
}

/** False when Create invoice, a retry or an invoice row holds focus;
 *  true while loading, or with no invoices and nothing focusable below. */
export function isPreviouslyInvoicedPanelFocusable({
    isLoading,
    isError,
    invoiceCount,
    canAddInvoice,
    soldLines,
}: PreviouslyInvoicedPanelState): boolean {
    if (canAddInvoice) return false;
    if (stillToInvoiceHasFocusable(soldLines)) return false;
    if (isLoading) return true;
    if (isError) return false;
    return invoiceCount === 0;
}

export function isInvoicingPanelFocusable({
    view,
    canBrowseInvoices,
    raise,
    previouslyInvoiced,
}: {
    view: InvoicingView | 'loading';
    canBrowseInvoices: boolean;
    raise: RaisePanelState;
    previouslyInvoiced: PreviouslyInvoicedPanelState;
}): boolean {
    if (view === 'loading') return true;
    if (view === 'previously_invoiced') return isPreviouslyInvoicedPanelFocusable(previouslyInvoiced);
    return !canBrowseInvoices && isRaisePanelFocusable(raise);
}
